import { defineComponent as _defineComponent } from 'vue'
import type { UpcomingEvent } from '@/features/sports/types/interfaces';
import type { MultiStepSportEvent, SingleStepSportEvent, SportCompetitorV2 } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type {
	GetSingleStepSportOverviewQuery,
	GetSingleStepSportOverviewQueryVariables,
} from '@/features/sports/graphql/queries/GetSingleStepSportOverview.query';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import GlobalSportsHeaderCard from '@/features/global-home/components/sports/GlobalSportsHeaderCard.vue';
import MultiStepEventEncounter from '@/features/sports/components/global/MultiStepEventEncounter.vue';

import { GetSingleStepSportOverviewDocument } from '@/features/sports/graphql/queries/GetSingleStepSportOverview.query';
import { HomepageSports } from '@/features/sports/types/sports-types';
import { ObjectType, Platform, SportEventAggregation, SportEventStatus, SportType } from '@/@types/graphql-types';
import { SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { WebLocale } from '@/enums/web-locale';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { isMultiStepSportEvent } from '@/features/sports/types/enums';
import { toastError } from '@/helpers/vfm/toast-helper';

import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';
import { useQuery } from '@/helpers/composables/useApollo';
import { useRoute } from 'vue-router/composables';

import { computed } from 'vue';

type TimeGroup = 'live' | 'today' | 'upcoming';


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalSportsCards',
  props: {
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { additionalContexts: SnowplowContext[] };



const route = useRoute();
const { t, tc } = useI18n();
const { todayDate } = usePersistedTodaysDate();
const { localeObject, language, locale, country, webLocale } = useLanguageStore();

const { result: sportEvents, onError } = useQuery<
	GetSingleStepSportOverviewQuery,
	GetSingleStepSportOverviewQueryVariables
>(
	GetSingleStepSportOverviewDocument,
	() => ({
		platform: Platform.Web,
		language: language.value,
		country: country.value,
		first: HomepageSports.length,
		bucketSize: 10, // @todo check if we want to keep it for multi sport queries
		groupBy: SportEventAggregation.Sport,
		filter: {
			startAt: {
				from: todayDate.value.toISOString(),
				to: null,
			},
			competitionIds: [],
			sports: HomepageSports,
		},
		timezoneOffset: 0,
	}),
	() => ({ enabled: route.name === 'app.home', errorPolicy: 'all' })
);

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Home Upcoming Sports Events Data]:',
		{ error, where: 'Component: GlobalSportsV2Cards' },
		'error'
	);

	if (process.client) toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
});

const upcomingEvents = computed(() => {
	const results = {} as Record<(typeof HomepageSports)[number], any[]>;

	(sportEvents.value?.sportEventBucketsV2.edges ?? [])
		.flatMap(bucket => bucket.node.edges ?? [])
		.forEach(({ node }) => (results[node.sport] ??= []).push(node));

	return results;
});

const sportOrder = computed(() => {
	if (webLocale.value === WebLocale.us)
		return [
			SportType.AmericanFootball,
			SportType.Basketball,
			SportType.Baseball,
			SportType.IceHockey,
			SportType.Football,
			SportType.Tennis,
		];

	return [
		SportType.Football,
		SportType.Tennis,
		SportType.AmericanFootball,
		SportType.Basketball,
		SportType.Baseball,
		SportType.IceHockey,
	];
});

const sportCardData = computed(() => {
	const data = [
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_AMERICAN_FOOTBALL'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/american_football.png`,
			sport: SportType.AmericanFootball,
			events: getSportEvents(upcomingEvents.value.AMERICAN_FOOTBALL),
		},
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_BASKETBALL'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/basketball.png`,
			sport: SportType.Basketball,
			events: getSportEvents(upcomingEvents.value.BASKETBALL),
		},
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_BASEBALL'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/baseball.png`,
			sport: SportType.Baseball,
			events: getSportEvents(upcomingEvents.value.BASEBALL),
		},
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_ICE_HOCKEY'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/ice_hockey.png`,
			sport: SportType.IceHockey,
			events: getSportEvents(upcomingEvents.value.ICE_HOCKEY),
		},
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_FOOTBALL'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/football.png`,
			sport: SportType.Football,
			events: getSportEvents(upcomingEvents.value.FOOTBALL),
		},
		{
			name: tc('SPORTS_WEBAPP_SPORT_TYPE_TENNIS'),
			icon: `/${ASSETS_DIR}/img/sports/icons/sport/tennis.png`,
			sport: SportType.Tennis,
			events: getSportEvents(upcomingEvents.value.TENNIS),
		},
	];

	return data.sort((a, b) => sportOrder.value.indexOf(a.sport) - sportOrder.value.indexOf(b.sport));
});

// depending on where the JS machine is, this can be different.
// so we force it by comparing it to the timezone of the current webLocale.
function changeTimezone(timeZone: string, targetDate = new Date()) {
	// we can hard code en-US because in the end we're only interested in the Date object
	const localisedDateString = targetDate.toLocaleDateString('en-US', { timeZone, timeZoneName: 'short' });
	return new Date(localisedDateString);
}

function getEvents(events: SingleStepSportEvent[]): Record<TimeGroup, UpcomingEvent[]> {
	const eventsByTimeGroup: Record<TimeGroup, UpcomingEvent[]> = { live: [], today: [], upcoming: [] };

	if (events.length === 0) return eventsByTimeGroup;

	const allEvents = events.map(
		event =>
			({
				id: event.id,
				lineItems: getLineItems(event),
				fullPath: getEventContent(event).fullPath,
				startAt: new Date(event.content.startAt),
				// needs to be put in current webLcoale's timezone
				localStartAt: changeTimezone(localeObject.value.timezone, new Date(event.content.startAt)),
				sport: event.sport,
				competition: {
					text: event.competition?.content?.name,
					fullPath: event.competition?.content?.fullPath,
				},
				competitionStageText: getCompetitionStageText(event),
				offers: event?.offers,
				watchNowOffer: event.watchNowOffer,
				// used for snowplow context and tracking
				sportEvent: event,
			} as UpcomingEvent)
	);

	const localeToday = changeTimezone(localeObject.value.timezone, todayDate.value);

	allEvents.forEach(event => {
		const eventIsToday = localeToday.toDateString() === event.localStartAt.toDateString();

		if (event?.sportEvent?.content?.status === SportEventStatus.Live) {
			eventsByTimeGroup.live.push(event);
		} else if (eventIsToday) {
			eventsByTimeGroup.today.push(event);
		} else {
			eventsByTimeGroup.upcoming.push(event);
		}
	});

	return eventsByTimeGroup;
}

function getSportEvents(events: SingleStepSportEvent[] = []) {
	const { live, today, upcoming } = getEvents(events);

	return live.length > 0 ? live : today.length > 0 ? today : upcoming;
}

function handleCardClick(event: any) {
	const sportContext = SnowplowSportContext.fromSportEventV2(event.sport, ObjectType.SportEvent, event.sportEvent);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'event_clicked',
			property: 'card-v2',
		},
		[sportContext, ...props.additionalContexts]
	);
}

function getEventContent(event: SingleStepSportEvent | MultiStepSportEvent) {
	if (isMultiStepSportEvent(event)) {
		return event.parent?.content ?? event.content;
	}

	return event.content;
}

function competitorToLineItem(competitor: SportCompetitorV2) {
	return {
		text: competitor!.content.name,
		iconUrl: competitor!.content.iconUrl as string,
		iconBlurHash: competitor!.content.iconBlurHash as string,
	};
}

// Depending on sport, it can the team's name or the course of the event.
// That's why it's called line items.
function getLineItems(event: SingleStepSportEvent | MultiStepSportEvent): UpcomingEvent['lineItems'] {
	if (isMultiStepSportEvent(event)) {
		return [{ text: event.parent?.content.name ?? '' }];
	}

	const { competitors } = event as SingleStepSportEvent;
	const teams = [competitors.homeTeam, competitors.awayTeam];

	return teams.flatMap(team => {
		// tennis doubles
		if (event.sport === SportType.Tennis && team.competitors.length === 2) {
			return team.competitors.map(competitor => competitorToLineItem(competitor));
		}

		// non-tennis teams have only 1 entry
		return competitorToLineItem(team.competitors.find(Boolean)!);
	});
}

function getCompetitionStageText(event: SingleStepSportEvent | MultiStepSportEvent) {
	const { name, number } = event.content?.competitionStage || {};

	switch (event.sport) {
		case SportType.FormulaRacing:
			// capitalise racing (e.g. Practice, Qualifying, ...)
			const formulaRacingName = `${name?.slice(0, 1).toUpperCase()}${name?.slice(1).toLowerCase()}`;
			return [formulaRacingName, number].filter(item => item !== null).join(' ');

		default:
			if (name === 'UNDEFINED' || !name) return '';
			return t(`SPORTS_WEBAPP_${name}`, { value: number });
	}
}

return { __sfc: true,props, route, t, tc, todayDate, localeObject, language, locale, country, webLocale, sportEvents, onError, upcomingEvents, sportOrder, sportCardData, changeTimezone, getEvents, getSportEvents, handleCardClick, getEventContent, competitorToLineItem, getLineItems, getCompetitionStageText, HiddenHorizontalScrollbar, GlobalSportsHeaderCard, MultiStepEventEncounter }
}

})